<template>
  <v-container fluid>
    <v-row dense>
      <v-col cols="12">

        <Dialog
        :stateId="dialogId"
        :maxWidth="dialogWidth"
        @click:outside="close"
        @dialog-closing="close"
        @close="close">
          <MitigationHabitatDetail
          v-if="viewing"
          :propMitigationHabitat="mitigationHabitat"
          @edit="editFromDetail"/>
          <ConfirmDelete
          v-else-if="confirmDeleteDialog"
          maxWidth="30em"
          @delete="performDelete"
          @cancel-delete="close"
          :title="$t('mitigationHabitat')"/>
          <MitigationHabitatForm
          v-else
          :propMitigationHabitat="mitigationHabitat"
          @mitigation-habitat-mutated="refresh" />
        </Dialog>

        <v-row>
          <v-col>
            <v-row align="center" no-gutters>
              <v-icon color="primary" class="mr-2">mdi-pine-tree</v-icon>
              <span class="text-h6 font-weight-regular">{{ $t('mitigationHabitats') }}</span>
            </v-row>
            <p class="text-caption mb-0">{{ $t('mitigationHabitatsSubtitle') }}</p>
            <DataTable
            class="mt-n6"
            showPointerCursor
            :customCells="customColumns"
            :headers="headers"
            :items="allMitigationHabitats"
            :loading="loading"
            @click:row="rowClicked">

              <template #description="{ item }">
                <span v-if="hasText(item.description)">{{ preview(item.description) }}</span>
                <span v-else class="font-italic grey--text">{{ $t('noneSpecified') }}</span>
              </template>

              <template #mitigationSteps="{ item }">
                <span v-if="hasText(item.mitigationSteps)">{{ preview(item.mitigationSteps) }}</span>
                <span v-else class="font-italic grey--text">{{ $t('noneSpecified') }}</span>
              </template>

              <template #related="{ item }">
                <Icon
                icon="mdi-information-outline"
                :tooltipText="$t('itemDetails', { item: $t('mitigationHabitat') })"
                @icon-clicked="openDetail(item)"
                elementId="mitigation-habitat-view"/>
              </template>

              <template #actions="{ item }">
                <Icon
                icon="mdi-pencil"
                :tooltipText="$t('edit')"
                @icon-clicked="mutate(item, 'edit')"
                :disabled="!isSustainabilityAdmin"
                elementId="mitigation-habitat-edit"/>
                <Icon icon="mdi-delete-forever"
                :tooltipText="$t('delete')"
                @icon-clicked="mutate(item, 'delete')"
                :disabled="!isSustainabilityAdmin"
                elementId="mitigation-habitat-delete"/>
              </template>

              <template #custom-controls>
                <Icon
                icon="mdi-refresh"
                :tooltipText="$t('refresh')"
                @icon-clicked="refresh"
                :small="false"/>
                <v-tooltip bottom color="grey" :disabled="endangeredSpeciesEnabled()">
                  <template #activator="{ on }">
                    <span v-on="on">
                      <Icon icon="mdi-plus"
                      :tooltipText="endangeredSpeciesEnabled() ? $t('addMitigationHabitat') : undefined"
                      @icon-clicked="createClicked"
                      :small="false"
                      :disabled="!canCreate"
                      elementId="mitigation-habitat-create"/>
                    </span>
                  </template>
                  <span class="subtitle-1 white--text">{{ $t('addHabitatRequiresEndangeredSpeciesMode') }}</span>
                </v-tooltip>
              </template>

            </DataTable>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { cloneDeep, truncate } from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import { UserClaims } from '@/utils/Enumerations.js'
import MitigationHeaders from '@/headers/Mitigation'
import { userAssignedClaim } from '@/utils/ClaimUtility'
import { uniqueDialogId } from '@/utils/componentHelpers'
import { DialogWidth } from '@/utils/Dialog.js'
import { endangeredSpeciesEnabled } from '@/utils/dueDiligence/mode.js'

export default {
  name: 'MitigationHabitatSettings',

  components: {
    ConfirmDelete: () => import('@/components/helper/ConfirmDelete.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    DataTable: () => import('@/components/core/table/DataTable.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    MitigationHabitatForm: () => import('@/components/settings-components/tract-settings/mitigation-habitat/MitigationHabitatForm.vue'),
    MitigationHabitatDetail: () => import('@/components/settings-components/tract-settings/mitigation-habitat/MitigationHabitatDetail.vue')
  },

  created () {
    this.refresh()
  },

  computed: {
    ...mapGetters('mitigation-habitat', ['allMitigationHabitats']),

    isSustainabilityAdmin () {
      return userAssignedClaim(UserClaims.SustainabilityAdmin)
    },

    canCreate () {
      return this.isSustainabilityAdmin && endangeredSpeciesEnabled()
    },

    headers () {
      return MitigationHeaders.mitigationHabitatHeaders()
    },

    dialogWidth () {
      return this.viewing ? DialogWidth.SimpleWide : '30em'
    },

    customColumns () {
      return [
        {
          slotName: 'description',
          value: 'description'
        },
        {
          slotName: 'mitigationSteps',
          value: 'mitigationSteps'
        },
        {
          slotName: 'related',
          value: 'related'
        },
        {
          slotName: 'actions',
          value: 'actions'
        }
      ]
    }
  },

  data: () => ({
    confirmDeleteDialog: false,
    viewing: false,
    loading: false,
    mitigationHabitat: undefined,
    previewLength: 60,
    dialogId: uniqueDialogId('mitigation-habitat-settings-dialog')
  }),

  methods: {
    ...mapActions('mitigation-habitat', ['fetchMitigationHabitats', 'deleteMitigationHabitat']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),

    endangeredSpeciesEnabled,

    hasText (value) {
      return !!value?.trim()
    },

    preview (text) {
      return truncate(text.replace(/\s+/g, ' ').trim(), { length: this.previewLength, separator: ' ' })
    },

    refresh () {
      this.close()
      this.loading = true
      this.fetchMitigationHabitats().finally(() => {
        this.loading = false
      })
    },

    createClicked () {
      if (!endangeredSpeciesEnabled()) {
        this.setSnackError(this.$t('addHabitatRequiresEndangeredSpeciesMode'))
        return
      }
      this.mutate(null, 'create')
    },

    mutate (mitigationHabitat, mutationType) {
      this.mitigationHabitat = mitigationHabitat ? cloneDeep(mitigationHabitat) : undefined
      if (mutationType === 'delete') this.confirmDeleteDialog = true
      this.openOrUpdateDialog(this.dialogId)
    },

    openDetail (mitigationHabitat) {
      this.mitigationHabitat = mitigationHabitat
      this.viewing = true
      this.openOrUpdateDialog(this.dialogId)
    },

    rowClicked ({ data }) {
      this.openDetail(data)
    },

    editFromDetail (mitigationHabitat) {
      this.viewing = false
      this.mutate(mitigationHabitat, 'edit')
    },

    close () {
      this.confirmDeleteDialog = false
      this.viewing = false
      this.closeDialogsAtOrAbove(this.dialogId)
      this.mitigationHabitat = undefined
    },

    async performDelete () {
      await this.deleteMitigationHabitat(this.mitigationHabitat.mitigationHabitatId)
      this.setSnack(this.$t('mitigationHabitatDeleted'))
      this.refresh()
    }
  }
}
</script>
